import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import util from 'utils/utils';
import api from 'api';

const UserAvatar = styled.div`
  position: absolute;
  background-color:white;
  border-radius:50% !important;
  height:  80px;
  width: 80px;
  right:0;
  left:0;
  bottom: 0;
  top:0;
  margin: auto;
  ${({ user }) => ((user.profile?.avatar || (!user.profile.firstName || !user.profile.lastName)) ? `
    background-size: cover;
    background-position: center center;
    background-image: url(${util.avatarUrl(user)});
    border: 1px solid white;
  ` : `
    background-color: #184258fc;
    > span {
      display: block;
      line-height: 80px;
      color: white;
      font-weight: bold;
      text-align: center;
      font-size: 18px;
    }
  `)}
`;

const VideoContainer = styled.div`
  position: relative;
  background-color: black;
  height: inherit;
  margin: auto;
  width: ${({ cameraOff }) => ((cameraOff) ? '30vw' : 'fit-content')};
  ${({ isMinimised }) => (isMinimised ? `
    width: ${({ cameraOff }) => (cameraOff) && '290px'};
  ` : `
    min-width: ${({ cameraOff, sharing }) => (cameraOff && sharing) && '100%'};
    max-width: ${({ cameraOff, sharing }) => (cameraOff && sharing) && '100%'};
    border-radius: 4px;
    margin-top: 5px;
    margin-bottom: 5px;
  `)}
`;

const VideoDiv = styled.video`
  height: inherit;
  max-width: 100%;
  transform: scale(-1, 1) !important;
  ${({ minimised, talking }) => (!minimised && !talking) && `
   border-radius: 4px;
  `}
`;

const NameContainer = styled.div`
  position: absolute;
  bottom: 0px;
  padding: 2px;
  border-radius: 2px;
  background-color: rgb(15 21 25 / 99%);
  > span {
    color: white;
    font-size: 12px;
  }
`;

const TalkingIcon = styled(Icon)`
  position: absolute;
  z-index: 100;
  background-color: rgb(31 100 160 / 98%) !important;
  color: white;
  right: 0px;
  top: 4px;
`;

const Participant = ({
  participant, localTracks, localCameraOff = false, isMinimised, localMuted = false, localUser = false, videoRefShare, setSharing, isSharing, audioStats, setLocalNetworkQuality,
}) => {
  const [videoTracks, setVideoTracks] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [user, setUser] = useState(null);
  const [talking, setTalking] = useState(false);
  const videoRef = useRef();
  const audioRef = useRef();

  const trackpubsToTracks = (trackMap) => Array.from(trackMap.values()).map((publication) => publication.track).filter((track) => track !== null);

  useEffect(() => {
    if (!localUser) return;

    if (localMuted) {
      participant.audioTracks.forEach((publication) => {
        participant.unpublishTrack(publication.track);
      });
    } else if (audioTracks.length === 0) {
      // if mic was off before join
      setAudioTracks([localTracks.audio], () => {
        audioTracks.forEach((track) => {
          participant.publishTrack(track);
        });
      });
    } else {
      audioTracks.forEach((track) => {
        participant.publishTrack(track);
      });
    }
    setMuted(localMuted);
  }, [participant, localUser, localMuted, audioTracks, localTracks?.audio]);

  useEffect(() => {
    if (!localUser) return;

    if (localCameraOff) {
      participant.videoTracks.forEach((publication) => {
        participant.unpublishTrack(publication.track);
      });
    } else if (videoTracks.length === 0) {
      // if camera was off before join
      setVideoTracks([localTracks.video], () => {
        videoTracks.forEach((track) => {
          participant.publishTrack(track);
        });
      });
    } else {
      videoTracks.forEach((track) => {
        participant.publishTrack(track);
      });
    }
    setCameraOff(localCameraOff);
  }, [participant, localUser, localCameraOff, videoTracks, localTracks?.video]);

  useEffect(() => {
    api.users.get(participant.identity, (u) => { setUser(u); });

    // fires when a remote participant publishes a track
    const trackSubscribed = (track) => {
      if (track.kind === 'video') {
        setCameraOff(!track.isEnabled);
        if (track.name !== 'screenShare') {
          setVideoTracks((prevVideoTracks) => [track, ...prevVideoTracks]);
        } else {
          setVideoTracks((prevVideoTracks) => [...prevVideoTracks, track]);
        }
      } else {
        setAudioTracks((prevAudioTracks) => [...prevAudioTracks, track]);

        setMuted(!track.isEnabled);
        track.on('disabled', () => {
          setMuted(true);
        });
        track.on('enabled', () => {
          setMuted(false);
        });
      }
    };

    const trackUnsubscribed = (track) => {
      if (track.kind === 'video') {
        if (track.name !== 'screenShare') {
          setCameraOff(true);
        } else {
          track.detach();
        }
        setVideoTracks((prevVideoTracks) => prevVideoTracks.filter((v) => v !== track));
      } else {
        setAudioTracks((prevAudioTracks) => prevAudioTracks.filter((v) => v !== track));

        setMuted(true);
      }
    };

    // fire when local participant publish a track
    const trackPublished = (publication) => {
      if (localUser && publication.track.kind === 'video') {
        if (publication.track.name !== 'screenShare') {
          setVideoTracks((prevVideoTracks) => [publication.track, ...prevVideoTracks]);
        } else {
          setVideoTracks((prevVideoTracks) => [...prevVideoTracks, publication.track]);
        }
      }
    };

    const networkQualityUpdated = (networkQualityLevel) => {
      if (localUser) {
        setLocalNetworkQuality(networkQualityLevel);
      }
    };

    setVideoTracks(trackpubsToTracks(participant.videoTracks));
    setAudioTracks(trackpubsToTracks(participant.audioTracks));

    participant.on('trackSubscribed', trackSubscribed);
    participant.on('trackUnsubscribed', trackUnsubscribed);
    participant.on('trackPublished', trackPublished);
    participant.on('networkQualityLevelChanged', networkQualityUpdated);
    participant.on('reconnected', networkQualityUpdated);
    return () => {
      setVideoTracks([]);
      setAudioTracks([]);
      participant.removeAllListeners();
    };
  }, [participant, localUser, setLocalNetworkQuality]);

  useEffect(() => {
    const videoTrack = videoTracks[0];
    if (!videoTrack) return;
    videoTrack.attach(videoRef.current);
    return () => {
      videoTrack.detach();
    };
  }, [videoTracks, participant]);

  useEffect(() => {
    const videoTrackShare = videoTracks.find((i) => i.name === 'screenShare' && ((localUser && i.isStopped === false) || (!localUser && i.isEnabled === true)));
    if (!videoTrackShare) return;
    videoTrackShare.attach(videoRefShare.current);
    setSharing(participant.identity);
    return () => {
      videoTrackShare.detach();
      setSharing(false);
    };
  }, [videoTracks, participant, localUser, videoRefShare, setSharing]);

  useEffect(() => {
    const audioTrack = audioTracks[0];
    if (!audioTrack) return;
    audioTrack.attach(audioRef.current);
    return () => {
      audioTrack.detach();
    };
  }, [audioTracks]);

  useEffect(() => {
    if (localUser) return;

    setMuted(!audioTracks || audioTracks?.length === 0 || !audioTracks[0]?.isEnabled);
    if (videoTracks[0] && videoTracks[0].name !== 'screenShare') {
      setCameraOff(videoTracks[0]?.isStopped);
    } else {
      setCameraOff(true);
    }
  }, [audioTracks, videoTracks, localUser]);

  useEffect(() => {
    if (!audioTracks) return;
    if (localUser) {
      if (audioStats?.localAudioTrackStats[0]?.trackId === audioTracks[0]?.id) {
        if (audioStats?.localAudioTrackStats[0]?.audioLevel > 6000) {
          setTalking(true);
        } else setTalking(false);
      }
    } else {
      audioStats && audioStats.remoteAudioTrackStats.forEach((stat) => {
        if (stat.trackSid === audioTracks[0]?.sid) {
          if (stat.audioLevel > 6000) {
            setTalking(true);
          } else setTalking(false);
        }
      });
    }
  }, [audioStats, audioTracks, localUser]);

  return (
    <>
      <VideoContainer isMinimised={isMinimised} cameraOff={cameraOff} sharing={isSharing} style={{ outline: talking && '2px solid rgb(22 111 190 / 98%)' }}>
        {talking && <TalkingIcon name='microphone' circular />}
        {(cameraOff && user)
          && <UserAvatar user={user} >
            {(!user?.profile?.avatar && user?.profile.firstName && user?.profile.lastName)
              && (
                <span>{user.profile.firstName[0].toUpperCase()}{user.profile.lastName[0].toUpperCase()}</span>
              )
            }
          </UserAvatar>
        }
        <VideoDiv style={{ display: cameraOff && 'none' }} ref={videoRef} autoPlay={true} minimised={isMinimised ? 1 : 0} talking={talking ? 1 : 0} />
        <NameContainer>
          <span>{user?.profile?.fullName}</span>
          {muted && <Icon name='microphone slash' size='small' style={{ margin: 'auto', color: 'white', marginLeft: 5 }} />}
        </NameContainer>
        <audio ref={audioRef} autoPlay={true} />
      </VideoContainer>
    </>

  );
};

export default Participant;
